import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  CloseCircleFilled,
  CalendarOutlined,
  MessageOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { AnalyticsEventNameType, SmartAlertConfig, SmartAlertRunItem } from "@coeff/api";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Link, useParams } from "react-router-dom";
import remarkGfm from "remark-gfm";
import styled from "styled-components";

import { QueryCacheKey } from "../../../../api";
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  NewWindowNew,
  LoaderWithPerfTimings,
  FlexRow,
  Typography,
  FlexColumn,
  Paper,
  PaperContent,
  TextWithIcon,
  SettingsBars,
  AlertRunSparkleIcon,
  SlackIconCircle,
  Mail,
  Table,
  Tag,
  Tooltip,
  AlertScheduleIcon,
  AlertMessageIcon,
  AlertDeliveryIcon,
} from "../../../../components";
import { COLORS } from "../../../../constants";
import {
  getScheduleSummaryText,
  getSmartAlertSuccessfulDeliveryCount,
  sentryCapture,
  useApiContext,
  useSentryMountUnmountTransaction,
  useTrack,
} from "../../../../utils";
import { ShowMore } from "../../components/CollapsibleText";
import { useDashboardContext } from "../../Dashboard";

import { SmartAlertRunDetailModal } from "./SmartAlertRunDetailModal";

const SMART_ALERT_DETAILS_SIDEBAR_WIDTH = 350;

const Container = styled.div`
  padding: 20px 30px;
  line-height: 1.3;
  height: 100%;
  width: 100%;
  max-width: calc(100% - ${SMART_ALERT_DETAILS_SIDEBAR_WIDTH}px);
  overflow: scroll;
`;

const Sidebar = styled.div`
  width: ${SMART_ALERT_DETAILS_SIDEBAR_WIDTH}px;
  position: fixed;
  right: 0px;
  top: 0px;
  bottom: 0px;
  border-left: 1px solid ${COLORS.black15};
  background: white;
  font-size: 13px;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
  overflow: scroll;
  height: 100%;
`;

const SidebarContent = styled.div`
  overflow-y: auto;
`;

const SidebarHeader = styled.div`
  margin: 15px;
  margin-bottom: 20px;
`;

const SidebarSection = styled.div`
  padding: 12px;
  margin: 0 12px 8px 12px;
  background: ${COLORS.white};
  border-radius: 8px;
  border: 1px solid ${COLORS.black8};
`;

const SidebarSectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
`;

const SidebarSectionIcon = styled.div`
  margin-top: 2px;
`;

const SidebarSectionTitle = styled(Typography)`
  font-weight: 700;
`;

const SidebarSectionContent = styled.div`
  padding-left: 22px;
  font-size: 14px;
  line-height: 18px;
  color: ${COLORS.black85};
`;

const MarkdownContent = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: ${COLORS.black85};

  p {
    margin: 0 0 8px 0;
  }

  p:last-child {
    margin-bottom: 0;
  }

  ul,
  ol {
    margin: 8px 0;
    padding-left: 20px;
  }

  li {
    margin: 4px 0;
  }

  strong {
    font-weight: 600;
  }
`;

const SidebarSubsectionTitle = styled.span`
  font-weight: 600;
  display: block;
  margin-bottom: 2px;
`;

const SidebarPreviewImage = styled.img`
  width: 100%;
  border-radius: 4px;
  object-fit: cover;
  margin-bottom: 8px;
`;

const DeliveryRow = styled(FlexRow)`
  align-items: center;
  gap: 8px;
  padding: 4px 0;
`;

const DeliveryIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.black45};
`;

const StyledTag = styled(Tag)`
  display: inline-flex;
  gap: 4px;
  align-items: center;
  font-size: 16px;
  height: 24px;
  border: none;
  font-weight: 500;

  .text {
    color: ${COLORS.black85};
    font-size: 14px;
  }
`;

const TruncatedText = styled.div`
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ThumbnailImage = styled.img`
  width: 64px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
  background: ${COLORS.black8};
`;

const Subtitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

// Helper functions for smart alert run status
const getSmartAlertRunStatusColor = (status?: string): "green" | "red" | "orange" | "default" => {
  switch (status) {
    case "completed":
      return "green";
    case "failed":
      return "red";
    case "skipped":
      return "orange";
    case "trigger_not_met":
      return "orange";
    default:
      return "default";
  }
};

const getSmartAlertRunStatusLabel = (status?: string): string => {
  switch (status) {
    case "completed":
      return "Success";
    case "failed":
      return "Failed";
    case "skipped":
      return "Skipped";
    case "queued":
      return "Queued";
    case "running":
      return "Running";
    case "trigger_not_met":
      return "Trigger Not Met";
    default:
      return "Unknown";
  }
};

const getSmartAlertRunStatusIcon = (status?: string): React.ReactNode => {
  switch (status) {
    case "completed":
      return <CheckCircleFilled />;
    case "failed":
      return <CloseCircleFilled />;
    case "skipped":
      return <ExclamationCircleFilled />;
    case "trigger_not_met":
      return <ExclamationCircleFilled />;
    default:
      return <ExclamationCircleFilled />;
  }
};

const getSuccessfulAlertBreakdown = (run: SmartAlertRunItem): string[] => {
  if (run.status === "failed" || run.status === "trigger_not_met") {
    return [];
  }
  let emailCount = 0;
  let slackCount = 0;
  let teamsCount = 0;

  // Check fanout results first (per_row granularity)
  if (run.fanout_results && run.fanout_results.length > 0) {
    for (const result of run.fanout_results) {
      if (result.error) {
        continue;
      } // Skip failed fanout items
      emailCount += result.emails_sent ?? 0;
      slackCount += result.slack_sent ?? 0;
      teamsCount += result.ms_teams_sent ?? 0;
    }
  } else {
    // Fall back to run-level counts for non-fanout runs
    emailCount = run.emails_sent ?? 0;
    slackCount = run.slack_messages_sent ?? 0;
    teamsCount = run.ms_teams_messages_sent ?? 0;
  }

  const breakdown: string[] = [];
  if (emailCount > 0) {
    breakdown.push(`${emailCount} email${emailCount === 1 ? "" : "s"} sent`);
  }
  if (slackCount > 0) {
    breakdown.push(`${slackCount} Slack message${slackCount === 1 ? "" : "s"} sent`);
  }
  if (teamsCount > 0) {
    breakdown.push(`${teamsCount} Teams message${teamsCount === 1 ? "" : "s"} sent`);
  }
  return breakdown;
};

const getFailedAlertCount = (run: SmartAlertRunItem): number => {
  // If the entire run failed, count as 1 failure
  if (run.status === "failed") {
    return 1;
  }
  // Check for partial failures in fanout results
  if (run.fanout_results && run.fanout_results.length > 0) {
    return run.fanout_results.filter(result => result.error).length;
  }
  return 0;
};

const getFailedAlertBreakdown = (run: SmartAlertRunItem): string[] => {
  // If the entire run failed, show the run-level error
  if (run.status === "failed" && run.error_message) {
    return [run.error_message];
  }
  // Collect errors from individual fanout results
  if (run.fanout_results && run.fanout_results.length > 0) {
    return run.fanout_results.filter(result => result.error).map(result => result.error as string);
  }
  return [];
};

const getFirstImageUrl = (run: SmartAlertRunItem): string | undefined => {
  // For fanout runs, use the first fanout result's image_url
  if (run.fanout_results && run.fanout_results.length > 0) {
    const firstWithImage = run.fanout_results.find(result => result.image_url);
    if (firstWithImage) {
      return firstWithImage.image_url as string;
    }
  }
  // Fall back to run-level generated_image_url
  return run.generated_image_url;
};

type SmartAlertDetailsSidebarProps = {
  config: SmartAlertConfig;
  lastGeneratedImageUrl?: string;
};

const SmartAlertDetailsSidebar: React.FC<SmartAlertDetailsSidebarProps> = ({
  config,
  lastGeneratedImageUrl,
}) => {
  const scheduleText = config.schedule
    ? getScheduleSummaryText(config.schedule, undefined, {
        skipPrefix: true,
        capitalize: true,
      })
    : "On data change";

  // Build slack summary
  const slackSummary =
    config.send_info?.slack_enabled &&
    (config.send_info.slack_channels.length > 0 || config.send_info.slack_users.length > 0)
      ? [
          ...config.send_info.slack_channels.map(c => `#${c.channel_name}`),
          ...config.send_info.slack_users.map(u => `@${u.user_name}`),
        ].join(", ")
      : null;

  // Build email summary
  const emailSummary =
    config.send_info?.email_enabled && config.send_info.email_addresses.length > 0
      ? config.send_info.email_addresses.join(", ")
      : null;

  return (
    <SidebarContent>
      <SidebarHeader>
        <FlexColumn>
          <FlexRow style={{ justifyContent: "space-between" }}>
            <TextWithIcon
              title={
                <Typography variant="body1" fontWeight={700}>
                  Settings
                </Typography>
              }
              icon={<SettingsBars />}
            />
          </FlexRow>

          <Paper variant="info">
            <PaperContent size="xsmall">
              <Typography color={COLORS.black65}>
                <Typography fontWeight={600}>READ-ONLY</Typography>
                Open in spreadsheet to make changes.
              </Typography>
            </PaperContent>
          </Paper>
        </FlexColumn>
      </SidebarHeader>

      <div style={{ position: "relative", opacity: "0.5" }}>
        <SidebarSection>
          <SidebarSectionHeader>
            <SidebarSectionIcon>
              <AlertRunSparkleIcon style={{ width: 16, height: 16 }} />
            </SidebarSectionIcon>
            <SidebarSectionTitle>Summary</SidebarSectionTitle>
          </SidebarSectionHeader>
          <SidebarSectionContent>
            <FlexColumn gap={2}>
              <div>
                <SidebarSubsectionTitle>Trigger:</SidebarSubsectionTitle>
                {config.trigger_prompt ? (
                  <ShowMore maxLines={5}>
                    <MarkdownContent>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {config.trigger_prompt.trim()}
                      </ReactMarkdown>
                    </MarkdownContent>
                  </ShowMore>
                ) : (
                  "Not specified"
                )}
              </div>
              <div>
                <SidebarSubsectionTitle>Data:</SidebarSubsectionTitle>
                {config.content_prompt ? (
                  <ShowMore maxLines={5}>
                    <MarkdownContent>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {config.content_prompt.trim()}
                      </ReactMarkdown>
                    </MarkdownContent>
                  </ShowMore>
                ) : (
                  "Not specified"
                )}
              </div>
            </FlexColumn>
          </SidebarSectionContent>
        </SidebarSection>

        {/* When to send Section */}
        <SidebarSection>
          <SidebarSectionHeader>
            <SidebarSectionIcon>
              <AlertScheduleIcon style={{ fontSize: 16 }} />
            </SidebarSectionIcon>
            <SidebarSectionTitle>When to send</SidebarSectionTitle>
          </SidebarSectionHeader>
          <SidebarSectionContent>{scheduleText}</SidebarSectionContent>
        </SidebarSection>

        {/* Message Section */}
        <SidebarSection>
          <SidebarSectionHeader>
            <SidebarSectionIcon>
              <AlertMessageIcon style={{ fontSize: 16 }} />
            </SidebarSectionIcon>
            <SidebarSectionTitle>Message</SidebarSectionTitle>
          </SidebarSectionHeader>
          <SidebarSectionContent>
            {lastGeneratedImageUrl ? (
              <SidebarPreviewImage src={lastGeneratedImageUrl} alt="Alert preview" />
            ) : (
              <Typography color="textSecondary" fontSize="13px">
                No preview available
              </Typography>
            )}
          </SidebarSectionContent>
        </SidebarSection>

        {/* Send to... Section */}
        {config.send_info && (slackSummary || emailSummary) && (
          <SidebarSection>
            <SidebarSectionHeader>
              <SidebarSectionIcon>
                <AlertDeliveryIcon style={{ fontSize: 16 }} />
              </SidebarSectionIcon>
              <SidebarSectionTitle>Send to...</SidebarSectionTitle>
            </SidebarSectionHeader>
            <SidebarSectionContent>
              {slackSummary && (
                <DeliveryRow>
                  <DeliveryIcon>
                    <SlackIconCircle style={{ width: 14, height: 14 }} />
                  </DeliveryIcon>
                  <Typography fontSize="14px">{slackSummary}</Typography>
                </DeliveryRow>
              )}
              {emailSummary && (
                <DeliveryRow>
                  <DeliveryIcon>
                    <Mail style={{ width: 14, height: 14 }} />
                  </DeliveryIcon>
                  <Typography fontSize="14px">{emailSummary}</Typography>
                </DeliveryRow>
              )}
            </SidebarSectionContent>
          </SidebarSection>
        )}
      </div>
    </SidebarContent>
  );
};

export const SmartAlertDetailsPage = () => {
  useSentryMountUnmountTransaction({ name: "SmartAlertDetailsWebapp", op: "ui.load" });
  const { apiClient } = useApiContext();
  const track = useTrack();
  const { spreadsheets } = useDashboardContext();

  const { smartAlertId, ssId } = useParams<{ smartAlertId: string; ssId: string }>();

  const [selectedRun, setSelectedRun] = useState<SmartAlertRunItem | undefined>(undefined);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 25;
  const MAX_LIMIT = 100;

  const {
    data: smartAlertResponse,
    isLoading: isLoadingSmartAlert,
    isError: isErrorSmartAlert,
  } = useQuery(
    [QueryCacheKey.ALERTS, "smart_alert", smartAlertId],
    () => apiClient.getSmartAlert({ smart_alert_id: smartAlertId }),
    {
      onError: error => {
        sentryCapture({
          error,
          name: "SmartAlertDetails: fetch",
          context: { smart_alert_id: smartAlertId, ss_id: ssId },
        });
      },
    }
  );

  const {
    data: smartAlertRunsResponse,
    isLoading: isLoadingRuns,
    isError: isErrorRuns,
  } = useQuery(
    [QueryCacheKey.ALERT_RUN_HISTORY, "smart_alert", smartAlertId],
    () => apiClient.getSmartAlertRuns({ smart_alert_id: smartAlertId, limit: MAX_LIMIT }),
    {
      onError: error => {
        sentryCapture({
          error,
          name: "SmartAlertDetails: fetchRuns",
          context: { smart_alert_id: smartAlertId, ss_id: ssId },
        });
      },
    }
  );

  const smartAlert = smartAlertResponse?.data;
  const smartAlertConfig = smartAlert?.config;
  const runs = smartAlertRunsResponse?.data.runs ?? [];

  // Get the latest generated image — prefer whichever is more recent: run image or preview image
  const latestRunImageUrl = runs.map(run => getFirstImageUrl(run)).find(url => url !== undefined);
  const previewImageNewer =
    Boolean(smartAlert?.last_generated_image_url) &&
    (smartAlert?.last_generated_image_url_at
      ? new Date(smartAlert.last_generated_image_url_at).getTime()
      : 0) > (smartAlert?.last_run_dt ? new Date(smartAlert.last_run_dt).getTime() : 0);
  const latestGeneratedImageUrl = previewImageNewer
    ? smartAlert?.last_generated_image_url
    : latestRunImageUrl ?? smartAlert?.last_generated_image_url;

  const spreadsheet = spreadsheets?.find(ss => ss.google_sheets_file_id === ssId);

  useEffect(() => {
    if (smartAlert && spreadsheet) {
      track(AnalyticsEventNameType.SmartAlertViewed, {
        smart_alert_id: smartAlert.smart_alert_id,
        ss_id: spreadsheet.google_sheets_file_id,
        ss_name: spreadsheet.google_sheets_file_name,
      });
    }
  }, [smartAlert, spreadsheet]);

  const onOpenSheetClick = () => {
    track(AnalyticsEventNameType.SmartAlertOpenSheetClicked, {
      smart_alert_id: smartAlert?.smart_alert_id,
      ss_id: spreadsheet?.google_sheets_file_id,
      event_from: "smart_alert_details_webapp",
    });

    if (spreadsheet) {
      window.open(spreadsheet.spreadsheet_url, "_blank");
    }
  };

  const lastSentText = smartAlert?.last_run_dt
    ? `Last sent ${moment(smartAlert.last_run_dt).fromNow()}`
    : "Never sent";

  return (
    <>
      <Container>
        {isErrorSmartAlert || isErrorRuns ? (
          <div>
            <Paper variant="error">
              <PaperContent size="small">
                <FlexRow style={{ justifyContent: "space-between" }}>
                  <span>There was an error fetching your smart alert</span>

                  <Button type="text" onClick={() => history.back()}>
                    Back
                  </Button>
                </FlexRow>
              </PaperContent>
            </Paper>
          </div>
        ) : isLoadingSmartAlert || isLoadingRuns || smartAlert === undefined ? (
          <div style={{ margin: "5rem auto 2rem", textAlign: "center" }}>
            <LoaderWithPerfTimings name="SmartAlertDetails" size="large" />
          </div>
        ) : (
          <>
            <FlexColumn gap={3}>
              <Breadcrumb style={{ marginBottom: 0 }}>
                <BreadcrumbItem>
                  <Link to={{ pathname: `/dashboard/spreadsheets` }}>Spreadsheets</Link>
                </BreadcrumbItem>

                <BreadcrumbItem>
                  <Link
                    to={{
                      pathname: `/dashboard/spreadsheets/${spreadsheet?.google_sheets_file_id}`,
                      search: window.location.search,
                    }}
                  >
                    {spreadsheet?.google_sheets_file_name}
                  </Link>
                </BreadcrumbItem>

                <BreadcrumbItem>Smart Alerts</BreadcrumbItem>
              </Breadcrumb>

              <FlexRow style={{ justifyContent: "space-between" }}>
                <div>
                  <FlexRow style={{ alignItems: "center", gap: 8 }}>
                    <Typography fontSize="28px" fontWeight={800}>
                      {smartAlertConfig?.alert_name ?? "Smart Alert"}
                    </Typography>

                    {smartAlertConfig?.is_active === false ? (
                      <StyledTag className="no-border" color="orange">
                        <span className="text">Paused</span>
                      </StyledTag>
                    ) : null}
                  </FlexRow>

                  <Typography variant="body2" color="textSecondary">
                    {lastSentText}
                  </Typography>
                </div>

                {spreadsheet?.spreadsheet_url && (
                  <div>
                    <Button type="primary" style={{ height: "40px" }} onClick={onOpenSheetClick}>
                      <FlexRow gap={1.5}>
                        <span style={{ marginTop: 2 }}>Open Sheet</span>
                        <NewWindowNew />
                      </FlexRow>
                    </Button>
                  </div>
                )}
              </FlexRow>

              <Subtitle style={{ marginBottom: 12, marginTop: 16 }}>
                <Typography fontWeight="bold">Run History</Typography>

                <Typography variant="body3" color="textSecondary" fontWeight="normal">
                  Displaying last 30 days
                </Typography>
              </Subtitle>
            </FlexColumn>

            <Table
              style={{ overflow: "scroll" }}
              scroll={{ x: "auto", y: "auto" }}
              loading={false}
              locale={{ emptyText: "No smart alert runs in the last 30 days" }}
              dataSource={runs}
              rowKey="smart_alert_run_id"
              onRow={record => ({
                onClick: () => {
                  track(AnalyticsEventNameType.SmartAlertRunDetailOpened, {
                    smart_alert_id: smartAlertId,
                    smart_alert_run_id: record.smart_alert_run_id,
                  });
                  setSelectedRun(record);
                },
                style: { cursor: "pointer" },
              })}
              pagination={
                runs.length > PAGE_SIZE
                  ? {
                      position: ["bottomCenter"],
                      size: "small",
                      pageSize: PAGE_SIZE,
                      showSizeChanger: false,
                      onChange: newPage => {
                        track(AnalyticsEventNameType.SmartAlertRunHistoryPageChanged, {
                          smart_alert_id: smartAlertId,
                        });
                        setPage(newPage - 1);
                      },
                      current: page + 1,
                    }
                  : false
              }
              columns={[
                {
                  title: "",
                  key: "thumbnail",
                  width: 80,
                  render: (_, run: SmartAlertRunItem) => {
                    const imageUrl = getFirstImageUrl(run);
                    if (imageUrl) {
                      return <ThumbnailImage src={imageUrl} alt="Alert preview" />;
                    }
                    return null;
                  },
                },
                {
                  title: "Reasoning",
                  key: "reasoning",
                  render: (_, run: SmartAlertRunItem) => {
                    const summary = run.trigger_reasoning;
                    if (!summary) {
                      return <Typography color="textSecondary">-</Typography>;
                    }
                    return (
                      <Tooltip title={summary}>
                        <TruncatedText>{summary}</TruncatedText>
                      </Tooltip>
                    );
                  },
                },
                {
                  title: "Status",
                  key: "status",
                  render: (_, run: SmartAlertRunItem) => {
                    const statusColor = getSmartAlertRunStatusColor(run.status);
                    const statusLabel = getSmartAlertRunStatusLabel(run.status);
                    const statusIcon = getSmartAlertRunStatusIcon(run.status);

                    return (
                      <StyledTag color={statusColor}>
                        {statusIcon}
                        <span className="text">{statusLabel}</span>
                      </StyledTag>
                    );
                  },
                },
                {
                  title: "Time",
                  key: "time",
                  defaultSortOrder: "descend",
                  sorter: (a: SmartAlertRunItem, b: SmartAlertRunItem) => {
                    const dateA = a.completed_at ?? a.started_at ?? "";
                    const dateB = b.completed_at ?? b.started_at ?? "";
                    return dateA.localeCompare(dateB);
                  },
                  render: (_, run: SmartAlertRunItem) => {
                    const dateStr = run.completed_at ?? run.started_at;
                    if (dateStr) {
                      const date = moment(dateStr);
                      // Show "Today at HH:MM" if today, otherwise "MMM DD, YYYY at HH:MM AM/PM"
                      if (date.isSame(moment(), "day")) {
                        return `Today at ${date.format("h:mm A")}`;
                      }
                      return date.format("MMM D, YYYY [at] h:mm A");
                    }
                    return "-";
                  },
                },
                {
                  title: "Successful",
                  key: "successful_alerts",
                  width: 100,
                  render: (_, run: SmartAlertRunItem) => {
                    const count = getSmartAlertSuccessfulDeliveryCount(run);
                    const breakdown = getSuccessfulAlertBreakdown(run);
                    const text = `${count}`;

                    if (breakdown.length === 0) {
                      return <div>{text}</div>;
                    }

                    return (
                      <Tooltip title={breakdown.join(", ")}>
                        <span style={{ cursor: "help" }}>{text}</span>
                      </Tooltip>
                    );
                  },
                },
                {
                  title: "Failed",
                  key: "failed_alerts",
                  width: 100,
                  render: (_, run: SmartAlertRunItem) => {
                    const count = getFailedAlertCount(run);
                    const breakdown = getFailedAlertBreakdown(run);
                    const text = `${count}`;

                    if (breakdown.length === 0) {
                      return <div>{text}</div>;
                    }

                    return (
                      <Tooltip title={breakdown.join(", ")}>
                        <span style={{ cursor: "help" }}>{text}</span>
                      </Tooltip>
                    );
                  },
                },
              ]}
            />
          </>
        )}
      </Container>

      {smartAlertConfig ? (
        <Sidebar>
          <SmartAlertDetailsSidebar
            config={smartAlertConfig}
            lastGeneratedImageUrl={latestGeneratedImageUrl ?? smartAlert?.last_generated_image_url}
          />
        </Sidebar>
      ) : null}

      {selectedRun && getSmartAlertSuccessfulDeliveryCount(selectedRun) > 0 && (
        <SmartAlertRunDetailModal
          run={selectedRun}
          alertName={smartAlertConfig?.alert_name}
          smartAlertId={smartAlertId}
          onClose={() => setSelectedRun(undefined)}
        />
      )}
    </>
  );
};
